import React from "react";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const SECTIONS = [
  {
    h: "Who we are",
    p: "Citirex LLC (\"Citirex\", \"we\", \"us\") is a Los Angeles based performance marketing agency. We operate owned-and-operated lead generation funnels and contract with carriers, agencies and contractors as licensed lead vendors.",
  },
  {
    h: "What we collect",
    p: "On consumer-facing properties we operate, we collect information voluntarily submitted by users — including contact details, demographic data, household information and stated coverage interest. We also collect technical data such as IP address, device type, browser type, referral source and timestamps. On citirex.com itself we do not capture consumer lead data.",
  },
  {
    h: "How we use it",
    p: "Collected information is used to match users with qualified buyers (carriers, agencies, contractors) consistent with the user's stated intent and the consent provided at the point of submission. Information is retained for audit, regulatory and fraud-prevention purposes in line with industry standards (TCPA, CCPA, GLBA).",
  },
  {
    h: "Who we share it with",
    p: "We share user information with the licensed buyers identified at the point of consent. We do not sell user information to undisclosed third parties. Buyer identities are presented at the time of submission as required under one-to-one consent rules.",
  },
  {
    h: "Your rights",
    p: "California residents may exercise rights under the CCPA / CPRA, including the right to know, the right to delete and the right to opt out of sale or sharing. See our CCPA Opt-Out page for the dedicated mechanism. Residents of other jurisdictions with comparable laws may exercise equivalent rights.",
  },
  {
    h: "Security",
    p: "Submitted information is transmitted over TLS and stored in access-controlled systems. We maintain SOC2-style operational controls and review access quarterly.",
  },
  {
    h: "Contact",
    p: "Privacy inquiries should be submitted through the contact form on citirex.com/contact. Citirex LLC, 15165 Ventura Blvd, Suite 225, Sherman Oaks, CA 91403.",
  },
];

export default function Privacy() {
  return (
    <main data-testid="privacy-page">
      <PageHead
        title="Privacy Policy"
        description="How Citirex collects, uses and protects user information across our owned-and-operated lead generation properties."
        path="/privacy"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">Legal</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[96px]">
              Privacy{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                Policy
              </span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 font-body text-sm uppercase tracking-[0.2em] text-citirex-sub">
              Last updated · December 2025
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
          <div className="md:col-span-4">
            <p className="overline">Contents</p>
            <ol className="mt-6 space-y-2 border-t border-citirex-border pt-6">
              {SECTIONS.map((s, i) => (
                <li
                  key={s.h}
                  className="flex items-baseline gap-3 font-body text-sm text-citirex-ink"
                >
                  <span className="font-editorial italic text-citirex-sub">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.h}
                </li>
              ))}
            </ol>
          </div>
          <div className="md:col-span-8">
            <div className="border-t border-citirex-border">
              {SECTIONS.map((s, i) => (
                <article
                  key={s.h}
                  className="border-b border-citirex-border py-10"
                >
                  <p className="font-editorial text-3xl italic text-citirex-accent">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-3 font-display text-2xl font-medium text-citirex-ink md:text-3xl">
                    {s.h}
                  </h2>
                  <p className="mt-4 font-body text-base leading-relaxed text-citirex-sub md:text-lg">
                    {s.p}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
